import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Code,
  Plant,
  Wrench,
  Money,
  Rocket,
  Users,
  Certificate,
  Target,
  CheckCircle,
  ArrowRight,
  Briefcase,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const tvetPrograms = [
  {
    icon: Briefcase,
    title: "Entrepreneurship & Small Business Skills",
    description: "Comprehensive training in starting and managing small businesses, including business planning, marketing, and customer relations.",
    outcomes: ["Business plan development", "Marketing strategies", "Customer service excellence", "Record keeping"],
  },
  {
    icon: Code,
    title: "ICT & Digital Skills Training",
    description: "Building digital literacy and technical skills for the modern economy, from basic computer skills to advanced applications.",
    outcomes: ["Computer fundamentals", "Microsoft Office suite", "Internet and email", "Social media for business"],
  },
  {
    icon: Plant,
    title: "Agribusiness & Value Addition",
    description: "Training in modern agricultural practices, post-harvest handling, and value addition to increase farmer incomes.",
    outcomes: ["Modern farming techniques", "Post-harvest management", "Value addition processes", "Market access strategies"],
  },
  {
    icon: Wrench,
    title: "Construction & Vocational Trades",
    description: "Practical skills training in construction trades including masonry, carpentry, plumbing, and electrical work.",
    outcomes: ["Technical certifications", "Safety protocols", "Quality standards", "Tools and equipment handling"],
  },
  {
    icon: Money,
    title: "Financial Literacy & Business Management",
    description: "Essential financial skills for personal and business financial management, including budgeting, saving, and accessing credit.",
    outcomes: ["Personal budgeting", "Business finance management", "Savings and credit access", "Investment basics"],
  },
  {
    icon: Rocket,
    title: "Innovation & Startup Development",
    description: "Supporting young entrepreneurs to develop innovative business ideas and launch successful startups.",
    outcomes: ["Idea validation", "Minimum viable products", "Pitch development", "Investor readiness"],
  },
];

const impactStats = [
  { number: "2,500+", label: "Youth Trained" },
  { number: "85%", label: "Employment Rate" },
  { number: "500+", label: "Businesses Started" },
  { number: "15", label: "Training Centers" },
];

const benefits = [
  "Industry-aligned curriculum developed with employers",
  "Hands-on practical training with real-world projects",
  "Experienced instructors with industry backgrounds",
  "Career counseling and job placement support",
  "Certification recognized by industry partners",
  "Ongoing mentorship after program completion",
];

export default function TVETPage() {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28" data-testid="tvet-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/afobkmcc_SCHOOL.png')",
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-300 text-sm font-medium mb-6">
              Technical & Vocational Education Training
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6">
              Building Skills for Employment & Entrepreneurship
            </h1>
            <p className="text-lg text-green-50/90 leading-relaxed mb-8">
              PEDA is committed to equipping youth and communities with practical, income-generating skills through Technical and Vocational Education and Training (TVET) programs aligned with real market needs.
            </p>
            <Button 
              asChild 
              size="lg" 
              className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-8"
              data-testid="tvet-contact-button"
            >
              <Link to="/contact">
                Enroll Now <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-12 bg-white border-b border-slate-100" data-testid="tvet-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl sm:text-4xl font-bold text-green-700 mb-2">{stat.number}</div>
                <div className="text-slate-600 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="tvet-programs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Our Programs
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              TVET Training Programs
            </h2>
            <p className="text-lg text-slate-600">
              Choose from our range of practical skills training programs designed to meet market demands.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tvetPrograms.map((program, index) => (
              <motion.div
                key={program.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-green-700 transition-colors">
                  <program.icon className="h-7 w-7 text-green-700 group-hover:text-white transition-colors" weight="duotone" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{program.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{program.description}</p>
                
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Key Outcomes</p>
                  <div className="flex flex-wrap gap-2">
                    {program.outcomes.map((outcome) => (
                      <span key={outcome} className="px-3 py-1 bg-green-50 text-green-700 text-xs rounded-full">
                        {outcome}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our TVET */}
      <section className="py-20 md:py-28" data-testid="tvet-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                Why Choose PEDA TVET
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Industry-Aligned Training for Real-World Success
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Our TVET programs are designed in partnership with industry to ensure graduates have the skills employers need. We focus on practical, hands-on training that prepares students for immediate employment or entrepreneurship.
              </p>

              <div className="space-y-4">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-700 shrink-0 mt-0.5" weight="fill" />
                    <p className="text-slate-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0s4xigoz_ICT.png" 
                alt="TVET Training"
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl"
              />
              
              {/* Floating cards */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl max-w-xs border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Certificate className="h-6 w-6 text-amber-600" weight="duotone" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Certified Training</p>
                    <p className="text-sm text-slate-500">Industry-recognized certificates</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-6 -right-6 bg-green-700 rounded-2xl p-5 shadow-xl text-white">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Users className="h-6 w-6 text-white" weight="duotone" />
                  </div>
                  <div>
                    <p className="font-semibold">Job Placement</p>
                    <p className="text-sm text-green-100">85% employment rate</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Training Approach */}
      <section className="py-20 md:py-28 bg-green-950" data-testid="tvet-approach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">
              Our Approach
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              Comprehensive Training Model
            </h2>
            <p className="text-lg text-green-100/80">
              Our TVET approach combines classroom learning with practical experience for maximum impact.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: GraduationCap,
                title: "Theory & Foundations",
                description: "Solid theoretical foundation covering industry knowledge, safety protocols, and professional standards.",
              },
              {
                icon: Wrench,
                title: "Hands-On Practice",
                description: "Extensive practical training in well-equipped workshops and labs with real tools and equipment.",
              },
              {
                icon: Target,
                title: "Real-World Application",
                description: "Internships and apprenticeships with industry partners to apply skills in actual work environments.",
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="h-8 w-8 text-amber-400" weight="duotone" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-green-100/70 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28" data-testid="tvet-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Ready to Build Your Future?
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Join thousands of graduates who have transformed their lives through PEDA's TVET programs. Contact us today to learn about enrollment, scholarships, and training schedules.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-green-700 hover:bg-green-800 rounded-xl px-8"
              data-testid="tvet-enroll-button"
            >
              <Link to="/contact">
                Apply Now <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
              </Link>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="border-green-700 text-green-700 hover:bg-green-50 rounded-xl px-8"
              data-testid="tvet-programs-button"
            >
              <Link to="/projects">View Current Programs</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
